/**
 * Die Absenderauswahl im Dokumentblatt (`document-sender`, Spec 24c,
 * Oberfläche B) — dünner Wrapper in der Form von `EntityPicker`, aber über
 * `GET /senders?q=` (flache Liste, kein Baum; dieselbe Klasse wie
 * `features/contracts/TenantPicker`, nicht `EntityPicker` selbst, weil es
 * hier keinen Baum gibt).
 *
 * Seit Spec 46c auch der **Anbieter** eines Liefervertrags (`supply-create-
 * provider`): `testIdPrefix`, `noun`, `apiField`/`errorPrefix` und `allowNone`
 * stellen dieselbe Bauform auf die dortigen Bezeichner und Wörter um; die
 * Vorgaben sind der Dokumentblatt-Fall.
 *
 * Ersetzt seit 24c das Freitextfeld (Fachregel 1: „Kein Freitext-Absender
 * mehr in den Clients"). Ist das Dokument Altbestand ohne `sender_id`, zeigt
 * das geschlossene Feld weiterhin den Text (`legacyText`) mit dem Zusatz
 * „(Freitext)" — wählbar bleibt es trotzdem: Eine neue Auswahl überschreibt
 * ihn (Fachregel 7).
 */

import { useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { senderSubjectLabel } from '@/features/documents/labels'
import { useSenderOptions } from '@/features/documents/api'
import type { SenderRow } from '@/features/documents/api'
import { FIELD_CLASS } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'

export interface SenderSelection {
  readonly id: string
  readonly label: string
}

interface SenderFieldProps {
  readonly value: SenderSelection | null
  readonly legacyText: string | null
  readonly onSelect: (selection: SenderSelection | null) => void
  /** Öffnet `sender-sheet` im Anlegen-Modus, vorbelegt mit dem Suchtext
   * (Spec 24c, Oberfläche C: „vorbelegt mit dem Suchtext als Name"). */
  readonly onRequestNew: (searchText: string) => void
  readonly error: FieldError | undefined
  readonly disabled?: boolean
  /** Präfix aller Bezeichner (`<präfix>`, `-open`, `-search`, `-option-<id>`,
   * `-create`, `-none`, `-clear`). */
  readonly testIdPrefix?: string
  /** Das Wort in Beschriftung und Aktionen („Absender" / „Anbieter"). */
  readonly noun?: 'Absender' | 'Anbieter'
  /** Eigenschaft in der API-Antwort, an der ein Fehler hängt. */
  readonly apiField?: string
  /** `<art>-create`/`<art>-edit`: bestimmt mit `apiField` den Fehlerbezeichner. */
  readonly errorPrefix?: string
  /** Ohne „Kein …"-Eintrag und ohne „Entfernen" (ein Vertrag braucht einen Anbieter). */
  readonly allowNone?: boolean
}

/** Zweitzeile einer Option: Standard-Art · Standard-Objekt, nur die
 * vorhandenen Teile (Spec 24c, Oberfläche B). */
function optionHint(sender: Pick<SenderRow, 'default_category' | 'default_subject'>): string | null {
  const parts = [
    sender.default_category?.label,
    sender.default_subject !== null ? senderSubjectLabel(sender.default_subject) : undefined,
  ].filter((part): part is string => part !== undefined)
  return parts.length === 0 ? null : parts.join(' · ')
}

export function SenderField({
  value,
  legacyText,
  onSelect,
  onRequestNew,
  error,
  disabled = false,
  testIdPrefix = 'document-sender',
  noun = 'Absender',
  apiField = 'sender_id',
  errorPrefix = 'document',
  allowNone = true,
}: SenderFieldProps) {
  const [q, setQ] = useState('')
  const [isSearching, setIsSearching] = useState(value === null && legacyText === null)
  // Eine von außen gesetzte Auswahl (z. B. der neu angelegte Absender aus
  // `SenderSheet`) schließt die Suche — während des Renderns verglichen,
  // nicht in einem Effekt (dasselbe Muster wie `TenantPicker`).
  const [syncedValueId, setSyncedValueId] = useState(value?.id)
  if (value?.id !== syncedValueId) {
    setSyncedValueId(value?.id)
    setIsSearching(false)
    setQ('')
  }

  const optionsQuery = useSenderOptions(q, { enabled: isSearching })
  const hasError = error?.field === apiField
  const errorTestId = `${errorPrefix}-error-${apiField}`
  const hasCurrentValue = value !== null || legacyText !== null
  const closedLabel = value?.label ?? legacyText ?? `Kein ${noun}`

  function selectSender(selection: SenderSelection | null) {
    onSelect(selection)
    setIsSearching(false)
    setQ('')
  }

  return (
    <div className="flex flex-col gap-1" data-testid={testIdPrefix}>
      <span className="text-label text-muted">{noun}</span>

      {!isSearching ? (
        <div className={`flex items-center justify-between gap-2 ${FIELD_CLASS}`}>
          <span className={hasCurrentValue ? 'text-text truncate' : 'text-muted truncate'}>
            {closedLabel}
            {legacyText !== null && value === null && <span className="text-muted"> (Freitext)</span>}
          </span>
          {!disabled && (
            <span className="flex shrink-0 items-center gap-2">
              {hasCurrentValue && allowNone && (
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-clear`}
                  onClick={() => {
                    selectSender(null)
                  }}
                  className="text-accent text-label inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center hover:underline"
                >
                  Entfernen
                </button>
              )}
              <button
                type="button"
                data-testid={`${testIdPrefix}-open`}
                onClick={() => {
                  setIsSearching(true)
                }}
                className="text-accent text-label inline-flex min-h-(--tap-min) min-w-(--tap-min) items-center justify-center hover:underline"
              >
                Ändern
              </button>
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <input
            data-testid={`${testIdPrefix}-search`}
            type="search"
            aria-label={`${noun} suchen`}
            placeholder={`${noun} suchen`}
            value={q}
            aria-invalid={hasError}
            aria-describedby={hasError ? errorTestId : undefined}
            onChange={(event) => {
              setQ(event.target.value)
            }}
            className={`text-text ${FIELD_CLASS}`}
          />

          {optionsQuery.isPending && <SkeletonRows testId={`${testIdPrefix}-loading`} rows={4} />}

          {optionsQuery.isError && (
            <ErrorState
              testId={`${testIdPrefix}-error`}
              retryTestId={`${testIdPrefix}-retry`}
              error={optionsQuery.error}
              onRetry={() => {
                void optionsQuery.refetch()
              }}
            />
          )}

          {optionsQuery.isSuccess && optionsQuery.data.items.length === 0 && (
            <EmptyState
              testId={`${testIdPrefix}-empty`}
              title={q.trim() === '' ? `Noch kein ${noun} angelegt.` : `Kein ${noun} gefunden.`}
              action={
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-create`}
                  onClick={() => {
                    onRequestNew(q.trim())
                  }}
                  className="bg-accent rounded-card text-body px-4 text-accent-on"
                >
                  {q.trim() === '' ? `Ersten ${noun} anlegen` : `Neuer ${noun} …`}
                </button>
              }
            />
          )}

          {optionsQuery.isSuccess && optionsQuery.data.items.length > 0 && (
            <ul className="border-border rounded-card border">
              {allowNone && (
                <li className="border-border border-b">
                  <button
                    type="button"
                    data-testid={`${testIdPrefix}-none`}
                    onClick={() => {
                      selectSender(null)
                    }}
                    className="hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
                  >
                    <span className="text-body text-text">{`Kein ${noun}`}</span>
                  </button>
                </li>
              )}
              {optionsQuery.data.items.map((sender) => {
                const hint = optionHint(sender)
                return (
                  <li key={sender.id} className="border-border border-b">
                    <button
                      type="button"
                      data-testid={`${testIdPrefix}-option-${sender.id}`}
                      onClick={() => {
                        selectSender({ id: sender.id, label: sender.name })
                      }}
                      className="hover:bg-bg flex min-h-(--tap-min) w-full flex-col items-start justify-center gap-0.5 px-3 py-1 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
                    >
                      <span className="text-body text-text">{sender.name}</span>
                      {hint !== null && <span className="text-label text-muted">{hint}</span>}
                    </button>
                  </li>
                )
              })}
              <li>
                <button
                  type="button"
                  data-testid={`${testIdPrefix}-create`}
                  onClick={() => {
                    onRequestNew(q.trim())
                  }}
                  className="text-accent text-label hover:bg-bg flex min-h-(--tap-min) w-full items-center px-3 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
                >
                  {`Neuer ${noun} …`}
                </button>
              </li>
            </ul>
          )}
        </div>
      )}

      {hasError && (
        <p id={errorTestId} data-testid={errorTestId} role="alert" className="text-status-overdue">
          {error.message}
        </p>
      )}
    </div>
  )
}
